// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Logical-node hooks (ADR-0012).
 *
 * Backed by react-query (ADR-0008): the node list is small, changes only when
 * someone re-resolves membership, and is read from more than one screen.
 */

import { useQuery, queryOptions } from '@tanstack/react-query';
import { listLogicalNodes } from '../services/monetizationService';
import { useAuth } from './useAuth';
import type { LogicalNode } from '../types/monetization';

const NO_NODES: LogicalNode[] = [];

/**
 * Query options for a site's logical nodes. Shared so any future prefetch and
 * the hook below cannot disagree about the cache key.
 *
 * The key carries `orgId` as well as `siteId`: nodes are scoped to an org/site
 * pair upstream, and keying on the site alone would serve one org's nodes to
 * another if a user's org ever changes within a session.
 */
export function logicalNodesQueryOptions(orgId: string, siteId: string) {
  return queryOptions({
    queryKey: ['monetization', 'logical-nodes', orgId, siteId] as const,
    queryFn: () => listLogicalNodes(orgId, siteId).then(envelope => envelope.data ?? NO_NODES),
  });
}

export interface UseLogicalNodesResult {
  nodes: LogicalNode[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Every logical node for a site. Both ids are required — the query stays idle
 * until the profile resolves an organization and a site is selected, rather
 * than firing a request that can only 404.
 */
export function useLogicalNodes(siteId?: string): UseLogicalNodesResult {
  const { orgId } = useAuth();
  const enabled = Boolean(orgId && siteId);
  const query = useQuery({
    ...logicalNodesQueryOptions(orgId ?? '', siteId ?? ''),
    enabled,
  });

  return {
    nodes: query.data ?? NO_NODES,
    loading: enabled && query.isPending,
    error: query.isError ? 'Unable to load logical nodes for this site.' : null,
    refetch: () => {
      void query.refetch();
    },
  };
}
